/**
 * Компонент управления советами дня (админ-панель).
 * Список советов с быстрым переключением активности и диалогом создания/редактирования,
 * включая настройку расписания показа (месяцы, дни недели, интервалы чисел месяца).
 *
 * @module components/AdminDailyTips
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Snackbar,
  Alert,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  IconButton,
  Switch,
  FormControlLabel,
  InputAdornment,
  Chip,
  Divider
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Image as ImageIcon,
  TipsAndUpdates as TipsIcon
} from '@mui/icons-material';
import apiService from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import API_CONFIG from '../config/api.config';

/** Короткие названия месяцев (индекс 0 соответствует месяцу 1) */
const MONTH_NAMES = ['Янв', 'Фев', 'Мар', 'Апр', 'Май', 'Июн', 'Июл', 'Авг', 'Сен', 'Окт', 'Ноя', 'Дек'];

/**
 * Дни недели в формате бэкенда: 0 = воскресенье, 1 = понедельник, ..., 6 = суббота.
 * Порядок элементов в массиве определяет порядок отображения чипсов (с понедельника).
 */

const WEEK_DAYS = [
  { value: 1, label: 'Пн' },
  { value: 2, label: 'Вт' },
  { value: 3, label: 'Ср' },
  { value: 4, label: 'Чт' },
  { value: 5, label: 'Пт' },
  { value: 6, label: 'Сб' },
  { value: 0, label: 'Вс' }
];

/**
 * Форма совета дня (соответствует DailyTipPayload бэкенда).
 *
 * @typedef {Object} DailyTipForm
 * @property {string} title - Заголовок совета (до 100 символов)
 * @property {string} text - Текст совета
 * @property {string} imageUrl - URL изображения (пустая строка, если изображения нет)
 * @property {boolean} isActive - Признак активности
 * @property {number[]} months - Месяцы показа (1-12), пустой массив = без ограничений
 * @property {number[]} daysOfWeek - Дни недели показа (0-6, 0 = воскресенье), пустой массив = без ограничений
 * @property {Array<{from: number, to: number}>} monthDayRanges - Интервалы чисел месяца (1-31), пустой массив = без ограничений
 */

/** @type {DailyTipForm} Начальное состояние формы нового совета */
const emptyForm = {
  title: '',
  text: '',
  imageUrl: '',
  isActive: true,
  months: [],
  daysOfWeek: [],
  monthDayRanges: []
};

/**
 * Формирует полный URL изображения совета.
 * Относительный путь дополняется базовым URL сервера изображений.
 *
 * @param {string} imageUrl - URL или относительный путь изображения
 * @returns {string} Полный URL изображения или пустая строка
 */

const getFullImageUrl = (imageUrl) => {
  if (!imageUrl) return '';
  return imageUrl.startsWith('http') ? imageUrl : `${API_CONFIG.IMAGES_BASE_URL}${imageUrl}`;
};

/**
 * Переключает наличие значения в массиве (добавляет, если отсутствует, иначе удаляет).
 *
 * @param {number[]} list - Исходный массив значений
 * @param {number} value - Переключаемое значение
 * @returns {number[]} Новый массив с переключённым значением
 */

const toggleValue = (list, value) => list.includes(value) ? list.filter(v => v !== value) : [...list, value];

/**
 * Формирует человекочитаемое описание расписания показа совета.
 *
 * @param {Object} tip - Совет дня из API
 * @returns {string} Описание расписания или «Без ограничений (каждый день)»
 */

const getScheduleSummary = (tip) => {
  const parts = [];
  if (tip.months && tip.months.length > 0) {
    parts.push([...tip.months].sort((a, b) => a - b).map(m => MONTH_NAMES[m - 1]).join(', '));
  }

  if (tip.daysOfWeek && tip.daysOfWeek.length > 0) {
    parts.push(WEEK_DAYS.filter(d => tip.daysOfWeek.includes(d.value)).map(d => d.label).join(', '));
  }

  if (tip.monthDayRanges && tip.monthDayRanges.length > 0) {
    parts.push(tip.monthDayRanges.map(r => r.from === r.to ? `${r.from} число` : `${r.from}–${r.to} числа`).join(', '));
  }

  return parts.length > 0 ? parts.join('; ') : 'Без ограничений (каждый день)';
};

/**
 * Компонент управления советами дня.
 * Отображает список всех советов (включая неактивные) и позволяет создавать,
 * редактировать, удалять советы и переключать их активность.
 * Действия изменения доступны только ролям с правом редактирования (canEdit).
 *
 * @returns {JSX.Element} Раздел управления советами дня
 */

const AdminDailyTips = () => {
  const { canEdit } = useAuth();
  const [tips, setTips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const [tipDialogOpen, setTipDialogOpen] = useState(false);
  const [editingTip, setEditingTip] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [tipToDelete, setTipToDelete] = useState(null);

  /** Загружает список всех советов дня из API */
  const fetchTips = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiService.adminDailyTips.getAll();
      if (response.data.success) {
        setTips(response.data.data || []);
      } else {
        setSnackbar({ open: true, message: 'Не удалось загрузить советы', severity: 'error' });
      }
    } catch (error) {
      console.error('Ошибка загрузки советов дня:', error);
      setSnackbar({ open: true, message: 'Произошла ошибка при загрузке', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTips();
  }, [fetchTips]);

  /** Открывает диалог создания нового совета с пустой формой */
  const handleOpenCreate = () => {
    setEditingTip(null);
    setForm(emptyForm);
    setTipDialogOpen(true);
  };

  /**
   * Открывает диалог редактирования существующего совета,
   * заполняя форму его текущими значениями.
   *
   * @param {Object} tip - Редактируемый совет
   */

  const handleOpenEdit = (tip) => {
    setEditingTip(tip);
    setForm({
      title: tip.title || '',
      text: tip.text || '',
      imageUrl: tip.imageUrl || '',
      isActive: !!tip.isActive,
      months: tip.months || [],
      daysOfWeek: tip.daysOfWeek || [],
      monthDayRanges: (tip.monthDayRanges || []).map(r => ({ from: r.from, to: r.to }))
    });
    setTipDialogOpen(true);
  };

  /**
   * Валидирует форму и сохраняет совет: создаёт новый или обновляет существующий.
   * После успешного сохранения закрывает диалог и перезагружает список.
   */

  const handleSave = async () => {
    if (!form.title.trim() || !form.text.trim()) {
      setSnackbar({ open: true, message: 'Заполните заголовок и текст совета', severity: 'error' });
      return;
    }

    const invalidRange = form.monthDayRanges.some(r =>
      !Number.isInteger(r.from) || !Number.isInteger(r.to) || r.from < 1 || r.to > 31 || r.from > r.to
    );
    if (invalidRange) {
      setSnackbar({ open: true, message: 'Проверьте интервалы чисел месяца: числа от 1 до 31, "с" не больше "по"', severity: 'error' });
      return;
    }

    setSaving(true);
    try {
      const payload = {
        title: form.title,
        text: form.text,
        imageUrl: form.imageUrl,
        isActive: form.isActive,
        months: form.months,
        daysOfWeek: form.daysOfWeek,
        monthDayRanges: form.monthDayRanges
      };
      const response = editingTip
        ? await apiService.adminDailyTips.update(editingTip._id, payload)
        : await apiService.adminDailyTips.create(payload);

      if (response.data.success) {
        setTipDialogOpen(false);
        fetchTips();
        setSnackbar({ open: true, message: editingTip ? 'Совет обновлен' : 'Совет создан', severity: 'success' });
      } else {
        setSnackbar({ open: true, message: response.data.message || 'Не удалось сохранить совет', severity: 'error' });
      }
    } catch (error) {
      console.error('Ошибка сохранения совета:', error);
      setSnackbar({ open: true, message: error.response?.data?.message || 'Произошла ошибка при сохранении', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  /**
   * Переключает признак активности совета без открытия диалога редактирования.
   *
   * @param {Object} tip - Совет, у которого переключается активность
   */

  const handleToggleActive = async (tip) => {
    try {
      const response = await apiService.adminDailyTips.update(tip._id, { isActive: !tip.isActive });
      if (response.data.success) {
        setTips(prev => prev.map(t => t._id === tip._id ? { ...t, isActive: !tip.isActive } : t));
      } else {
        setSnackbar({ open: true, message: 'Не удалось изменить активность', severity: 'error' });
      }
    } catch (error) {
      console.error('Ошибка переключения активности совета:', error);
      setSnackbar({ open: true, message: 'Произошла ошибка', severity: 'error' });
    }
  };

  /** Удаляет совет, выбранный в диалоге подтверждения удаления */
  const handleDelete = async () => {
    if (!tipToDelete) return;
    try {
      const response = await apiService.adminDailyTips.delete(tipToDelete._id);
      if (response.data.success) {
        setTips(prev => prev.filter(t => t._id !== tipToDelete._id));
        setSnackbar({ open: true, message: 'Совет удален', severity: 'success' });
      } else {
        setSnackbar({ open: true, message: 'Не удалось удалить совет', severity: 'error' });
      }
    } catch (error) {
      console.error('Ошибка удаления совета:', error);
      setSnackbar({ open: true, message: 'Произошла ошибка при удалении', severity: 'error' });
    }
    setDeleteDialogOpen(false);
    setTipToDelete(null);
  };

  /**
   * Обновляет границу интервала чисел месяца в форме.
   *
   * @param {number} index - Индекс интервала в массиве monthDayRanges
   * @param {('from'|'to')} field - Изменяемая граница интервала
   * @param {string} rawValue - Значение из поля ввода (пустая строка или число)
   */

  const setRangeField = (index, field, rawValue) => {
    const value = rawValue === '' ? '' : Number(rawValue);
    setForm(prev => {
      const monthDayRanges = prev.monthDayRanges.map((r, i) => i === index ? { ...r, [field]: value } : r);
      return { ...prev, monthDayRanges };
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
            Советы дня
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Советы показываются в виджете «Совет дня» в мобильном приложении. Из подходящих по расписанию активных советов выбирается случайный.
          </Typography>
        </Box>
        {canEdit && (
          <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={handleOpenCreate}>
            Добавить совет
          </Button>
        )}
      </Box>

      {tips.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <TipsIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
          <Typography color="text.secondary">Советы пока не добавлены</Typography>
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {tips.map((tip) => (
            <Grid item xs={12} md={6} lg={4} key={tip._id}>
              <Card sx={{ height: '100%', opacity: tip.isActive ? 1 : 0.65 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    {tip.imageUrl && (
                      <Box
                        component="img"
                        src={getFullImageUrl(tip.imageUrl)}
                        sx={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 1, flexShrink: 0 }}
                        alt={tip.title}
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    )}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                        {tip.title}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden'
                        }}
                      >
                        {tip.text}
                      </Typography>
                    </Box>
                  </Box>

                  <Divider sx={{ my: 1.5 }} />

                  <Typography variant="caption" color="text.secondary" component="div">
                    Расписание: {getScheduleSummary(tip)}
                  </Typography>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          size="small"
                          checked={!!tip.isActive}
                          onChange={() => handleToggleActive(tip)}
                          disabled={!canEdit}
                        />
                      }
                      label={tip.isActive ? 'Активен' : 'Неактивен'}
                    />
                    {canEdit && (
                      <Box>
                        <IconButton size="small" onClick={() => handleOpenEdit(tip)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => { setTipToDelete(tip); setDeleteDialogOpen(true); }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/** Диалог создания/редактирования совета */}
      <Dialog open={tipDialogOpen} onClose={() => setTipDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editingTip ? 'Редактировать совет' : 'Новый совет дня'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Заголовок"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            margin="dense"
            inputProps={{ maxLength: 100 }}
            helperText={`${form.title.length}/100`}
          />
          <TextField
            fullWidth
            multiline
            minRows={3}
            label="Текст совета"
            value={form.text}
            onChange={(e) => setForm({ ...form, text: e.target.value })}
            margin="dense"
          />
          <TextField
            fullWidth
            label="URL изображения (необязательно)"
            value={form.imageUrl}
            onChange={(e) => setForm({ ...form, imageUrl: e.target.value })}
            margin="dense"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <ImageIcon />
                </InputAdornment>
              ),
            }}
          />
          {form.imageUrl && (
            <Box sx={{ mt: 1, textAlign: 'center' }}>
              <Box
                component="img"
                src={getFullImageUrl(form.imageUrl)}
                sx={{ maxWidth: '100%', maxHeight: 160, objectFit: 'cover', borderRadius: 1 }}
                alt="Превью"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            </Box>
          )}

          <FormControlLabel
            control={
              <Switch
                checked={form.isActive}
                onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
              />
            }
            label="Активен"
            sx={{ mt: 1, display: 'flex' }}
          />

          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" gutterBottom>
            Расписание показа
          </Typography>
          <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 2 }}>
            Если ничего не выбрано, совет может показываться в любой день. Если задано несколько ограничений, должны выполняться все.
          </Typography>

          <Typography variant="body2" sx={{ mb: 0.5 }}>Месяцы</Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
            {MONTH_NAMES.map((name, i) => {
              const value = i + 1;
              const selected = form.months.includes(value);
              return (
                <Chip
                  key={value}
                  label={name}
                  color={selected ? 'primary' : 'default'}
                  variant={selected ? 'filled' : 'outlined'}
                  onClick={() => setForm({ ...form, months: toggleValue(form.months, value) })}
                />
              );
            })}
          </Box>

          <Typography variant="body2" sx={{ mb: 0.5 }}>Дни недели</Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
            {WEEK_DAYS.map(({ value, label }) => {
              const selected = form.daysOfWeek.includes(value);
              return (
                <Chip
                  key={value}
                  label={label}
                  color={selected ? 'primary' : 'default'}
                  variant={selected ? 'filled' : 'outlined'}
                  onClick={() => setForm({ ...form, daysOfWeek: toggleValue(form.daysOfWeek, value) })}
                />
              );
            })}
          </Box>

          <Typography variant="body2" sx={{ mb: 0.5 }}>Интервалы чисел месяца</Typography>
          {form.monthDayRanges.map((range, index) => (
            <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <TextField
                type="number"
                label="С"
                size="small"
                value={range.from}
                onChange={(e) => setRangeField(index, 'from', e.target.value)}
                inputProps={{ min: 1, max: 31 }}
                sx={{ width: 100 }}
              />
              <TextField
                type="number"
                label="По"
                size="small"
                value={range.to}
                onChange={(e) => setRangeField(index, 'to', e.target.value)}
                inputProps={{ min: 1, max: 31 }}
                sx={{ width: 100 }}
              />
              <IconButton
                size="small"
                color="error"
                onClick={() => setForm({ ...form, monthDayRanges: form.monthDayRanges.filter((_, i) => i !== index) })}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={() => setForm({ ...form, monthDayRanges: [...form.monthDayRanges, { from: 1, to: 31 }] })}
          >
            Добавить интервал
          </Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTipDialogOpen(false)}>Отмена</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={saving}
            startIcon={saving ? <CircularProgress size={20} /> : null}
          >
            {editingTip ? 'Сохранить' : 'Создать'}
          </Button>
        </DialogActions>
      </Dialog>

      {/** Диалог подтверждения удаления совета */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Подтверждение удаления</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Вы действительно хотите удалить совет "{tipToDelete?.title}"?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Отмена</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Удалить
          </Button>
        </DialogActions>
      </Dialog>

      {/** Снэкбар с результатами операций */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminDailyTips;
